import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Card, Carousel, DrilldownModal, ExportButtonGroup,
  KpiCard, Tabs, useExport, useToast,
} from '@kss/ui';
import { useHasRole } from '@kss/auth';
import { useExportGate } from '../../hooks/useExportGate';
import {
  Store as StoreIcon, CheckCircle2, XCircle, Building2,
  RefreshCw, Lock, Search, X as XIcon, Printer, MapPin,
} from 'lucide-react';
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import type { Store } from '@kss/api';
import { storeService } from '@kss/api';
import { useThemeChartColors, DrillTable, type DrillColumn } from '../shared/dashboardHelpers';

type KpiId = 'total' | 'active' | 'inactive' | 'companies' | 'withAddress' | 'withCode';
interface KpiDef {
  id: KpiId; title: string; subtitle?: string; icon: React.ReactNode;
  compute: (r: Store[]) => { value: string | number };
  detail: (r: Store[]) => Store[];
}

const KPIS: KpiDef[] = [
  { id: 'total', title: 'Total stores', icon: <StoreIcon className="h-5 w-5 text-primary" />,
    compute: (r) => ({ value: r.length }), detail: (r) => r },
  { id: 'active', title: 'Active', icon: <CheckCircle2 className="h-5 w-5 text-success" />,
    compute: (r) => ({ value: r.filter((s) => s.isActive).length }),
    detail: (r) => r.filter((s) => s.isActive) },
  { id: 'inactive', title: 'Inactive', icon: <XCircle className="h-5 w-5 text-danger" />,
    compute: (r) => ({ value: r.filter((s) => !s.isActive).length }),
    detail: (r) => r.filter((s) => !s.isActive) },
  { id: 'companies', title: 'Companies', subtitle: 'Distinct', icon: <Building2 className="h-5 w-5 text-info" />,
    compute: (r) => ({ value: new Set(r.map((s) => s.companyId).filter(Boolean)).size }),
    detail: (r) => { const seen = new Set<string>(); return r.filter((s) => { const k = s.companyId ?? ''; if (!k || seen.has(k)) return false; seen.add(k); return true; }); } },
  { id: 'withAddress', title: 'With address', icon: <MapPin className="h-5 w-5 text-warning" />,
    compute: (r) => ({ value: r.filter((s) => !!s.address?.trim()).length }),
    detail: (r) => r.filter((s) => !!s.address?.trim()) },
  { id: 'withCode', title: 'With code', icon: <StoreIcon className="h-5 w-5 text-info" />,
    compute: (r) => ({ value: r.filter((s) => !!s.code?.trim()).length }),
    detail: (r) => r.filter((s) => !!s.code?.trim()) },
];

const DRILL_COLUMNS: DrillColumn<Store>[] = [
  { key: 'code', header: 'Code' },
  { key: 'name', header: 'Name' },
  { key: 'address', header: 'Address' },
  { key: 'isActive', header: 'Status',
    render: (_, row) => (
      <span className={row.isActive ? 'inline-flex rounded-full bg-success/15 px-2 py-0.5 text-xs text-success' : 'inline-flex rounded-full bg-danger/15 px-2 py-0.5 text-xs text-danger'}>
        {row.isActive ? 'Active' : 'Inactive'}
      </span>
    ) },
  { key: 'companyId', header: 'Company' },
];

type DrillContext = { title: string; subtitle?: string; rows: Store[] };
type TabId = 'kpis' | 'charts';

export default function StoreDashboard() {
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const canView = useHasRole(['admin', 'manager', 'staff']);
  const exportGate = useExportGate(['admin', 'manager']);
  const canExport = exportGate.enabled;

  const [rows, setRows] = useState<Store[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabId>('kpis');
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [openKpi, setOpenKpi] = useState<KpiDef | null>(null);
  const [chartDrill, setChartDrill] = useState<DrillContext | null>(null);
  const { exportToExcel, exportToPdf, printWidget } = useExport();
  const drillBodyRef = useRef<HTMLDivElement>(null);
  const chartDrillBodyRef = useRef<HTMLDivElement>(null);
  const dashboardRef = useRef<HTMLDivElement>(null);
  const [printing, setPrinting] = useState(false);
  const colors = useThemeChartColors();

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await storeService.all();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load stores';
      setError(msg); toastRef.current.error('Load failed', msg);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (canView) load(); }, [canView, load]);
  useEffect(() => { if (searchOpen) searchInputRef.current?.focus(); }, [searchOpen]);

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((s) =>
      (s.name ?? '').toLowerCase().includes(q) ||
      (s.code ?? '').toLowerCase().includes(q) ||
      (s.address ?? '').toLowerCase().includes(q)
    );
  }, [rows, query]);

  const kpis = useMemo(() => KPIS.map((def) => ({ def, ...def.compute(filteredRows) })), [filteredRows]);
  const kpiSlides = useMemo(() => {
    const chunks: (typeof kpis)[] = [];
    for (let i = 0; i < kpis.length; i += 4) chunks.push(kpis.slice(i, i + 4));
    return chunks.map((chunk, idx) => ({
      id: idx,
      content: (
        <div className="grid h-full grid-cols-1 gap-3 p-1 sm:grid-cols-2 lg:grid-cols-4">
          {chunk.map(({ def, value }) => (
            <KpiCard key={def.id} title={def.title} subtitle={def.subtitle} value={value} icon={def.icon} onClick={() => setOpenKpi(def)} />
          ))}
        </div>
      ),
    }));
  }, [kpis]);

  const statusData = useMemo(() => {
    const active = filteredRows.filter((s) => s.isActive);
    const inactive = filteredRows.filter((s) => !s.isActive);
    return [{ name: 'Active', value: active.length, detail: active }, { name: 'Inactive', value: inactive.length, detail: inactive }];
  }, [filteredRows]);

  const companyBarData = useMemo(() => {
    const map = new Map<string, Store[]>();
    for (const s of filteredRows) {
      const k = s.companyId || 'Unassigned';
      const l = map.get(k) ?? []; l.push(s); map.set(k, l);
    }
    return [...map.entries()]
      .map(([k, l]) => ({ name: k.length > 14 ? `${k.slice(0, 8)}…${k.slice(-4)}` : k, fullKey: k, count: l.length, detail: l }))
      .sort((a, b) => b.count - a.count).slice(0, 10);
  }, [filteredRows]);

  const rowsToExcel = (list: Store[]) => list.map((s) => ({
    Code: s.code ?? '', Name: s.name, Address: s.address ?? '',
    IsActive: s.isActive ? 'Active' : 'Inactive', companyId: s.companyId ?? '',
  }));
  const excelCols = [
    { key: 'Code', header: 'Code' }, { key: 'Name', header: 'Name' }, { key: 'Address', header: 'Address' },
    { key: 'IsActive', header: 'Status' }, { key: 'companyId', header: 'Company' },
  ];
  const kpiDrillRows = openKpi ? openKpi.detail(filteredRows) : [];
  const kpiExcel = () => openKpi && exportToExcel(rowsToExcel(kpiDrillRows), excelCols, `stores-${openKpi.id}`);
  const kpiPdf = () => openKpi && drillBodyRef.current && exportToPdf(drillBodyRef.current, `stores-${openKpi.id}`);
  const chartExcel = () => chartDrill && exportToExcel(rowsToExcel(chartDrill.rows), excelCols, `stores-${chartDrill.title.replace(/\s+/g, '-').toLowerCase()}`);
  const chartPdf = () => chartDrill && chartDrillBodyRef.current && exportToPdf(chartDrillBodyRef.current, chartDrill.title.replace(/\s+/g, '-').toLowerCase());
  const printDashboard = async () => { if (!dashboardRef.current) return; setPrinting(true); try { await exportToPdf(dashboardRef.current, 'stores-dashboard'); } finally { setPrinting(false); } };

  if (!canView) {
    return (
      <div className="-m-6 p-6">
        <div className="mx-auto mt-12 max-w-md rounded-xl border border-border bg-surface-card p-8 text-center">
          <Lock className="mx-auto h-8 w-8 text-content-muted" />
          <h2 className="mt-3 text-lg font-semibold text-content-primary">Access denied</h2>
          <p className="mt-1 text-sm text-content-muted">Your role does not permit viewing the Stores dashboard.</p>
        </div>
      </div>
    );
  }

  const kpisContent = <div className="space-y-3">{kpiSlides.length > 0 && <Carousel slides={kpiSlides} autoPlay={false} showArrows showDots height={200} />}</div>;
  const chartsContent = (
    <div className="grid gap-3 lg:grid-cols-2">
      <Card>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-content-primary">By status</h3>
          <span className="text-[11px] text-content-muted">Click a slice</span>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}
              onClick={(e: { payload?: { name: string; detail: Store[] } }) => e?.payload && setChartDrill({ title: `Stores by status — ${e.payload.name}`, subtitle: `${e.payload.detail.length} row(s)`, rows: e.payload.detail })}
              className="cursor-pointer">
              {statusData.map((d, i) => <Cell key={d.name} fill={colors[i % colors.length]} />)}
            </Pie>
            <Tooltip contentStyle={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => <span style={{ color: 'var(--color-text-secondary)' }}>{v}</span>} />
          </PieChart>
        </ResponsiveContainer>
      </Card>
      <Card>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-content-primary">Top companies</h3>
          <span className="text-[11px] text-content-muted">Click a bar</span>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={companyBarData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
            onClick={(e: { activePayload?: Array<{ payload: (typeof companyBarData)[number] }> }) => {
              const p = e?.activePayload?.[0]?.payload;
              if (p) setChartDrill({ title: `Stores in company — ${p.fullKey}`, subtitle: `${p.detail.length} row(s)`, rows: p.detail });
            }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="name" tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip contentStyle={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 12 }} cursor={{ fill: 'var(--color-bg-card-hover)' }} />
            <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={40} className="cursor-pointer">
              {companyBarData.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
  const tabs = [
    { id: 'kpis' as const, label: 'KPI Overview', content: kpisContent },
    { id: 'charts' as const, label: 'Charts', content: chartsContent },
  ];
  const activeContent = tabs.find((t) => t.id === tab)?.content;

  return (
    <div className="-m-6 space-y-3 p-[3px]">
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-surface-card px-4 py-3">
        {searchOpen ? (
          <div className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-md border border-primary bg-surface-card px-2">
            <Search className="h-3.5 w-3.5 shrink-0 text-content-muted" />
            <input ref={searchInputRef} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name, code or address..."
              onKeyDown={(e) => { if (e.key === 'Escape') { setSearchOpen(false); setQuery(''); } }}
              className="min-w-0 flex-1 bg-transparent text-sm text-content-primary outline-none placeholder:text-content-muted" />
            <button type="button" aria-label="Close search" onClick={() => { setSearchOpen(false); setQuery(''); }}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-content-muted hover:bg-surface-card-hover"><XIcon className="h-3.5 w-3.5" /></button>
          </div>
        ) : (<h2 className="min-w-0 flex-1 truncate text-base font-semibold text-content-primary">Stores dashboard</h2>)}
        <div className="flex shrink-0 items-center gap-2">
          {!searchOpen && (<button type="button" aria-label="Open search" onClick={() => setSearchOpen(true)} className="flex h-9 w-9 items-center justify-center rounded-md border border-border text-content-muted hover:bg-surface-card-hover"><Search className="h-4 w-4" /></button>)}
          {canExport && (<Button variant="outline" size="sm" onClick={printDashboard} disabled={printing}><Printer className={`h-3.5 w-3.5 ${printing ? 'animate-pulse' : ''}`} />{printing ? 'Printing…' : 'Print'}</Button>)}
          <Button variant="outline" size="sm" onClick={load} disabled={loading}><RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>
        </div>
      </div>
      {error && <Alert variant="danger">{error}</Alert>}
      <div ref={dashboardRef} className="space-y-3 bg-surface-page p-[3px]">
        <Tabs tabs={tabs} activeId={tab} onChange={(id) => setTab(id as TabId)} barOnly />
        <div>{activeContent}</div>
      </div>
      <DrilldownModal isOpen={!!openKpi} onClose={() => setOpenKpi(null)} title={openKpi ? `${openKpi.title} — details` : ''} subtitle={openKpi ? `${kpiDrillRows.length} row(s)` : ''} size="xl"
        actions={canExport ? <ExportButtonGroup onExcel={kpiExcel} onPdf={kpiPdf} onPrint={printWidget} compact /> : null}>
        <div ref={drillBodyRef}>
          <DrillTable rows={kpiDrillRows} columns={DRILL_COLUMNS} />
          {!canExport && kpiDrillRows.length > 0 && <p className="mt-3 text-[11px] text-content-muted">{exportGate.message}</p>}
        </div>
      </DrilldownModal>
      <DrilldownModal isOpen={!!chartDrill} onClose={() => setChartDrill(null)} title={chartDrill?.title ?? ''} subtitle={chartDrill?.subtitle} size="xl"
        actions={canExport ? <ExportButtonGroup onExcel={chartExcel} onPdf={chartPdf} onPrint={printWidget} compact /> : null}>
        <div ref={chartDrillBodyRef}>
          <DrillTable rows={chartDrill?.rows ?? []} columns={DRILL_COLUMNS} />
          {!canExport && (chartDrill?.rows.length ?? 0) > 0 && <p className="mt-3 text-[11px] text-content-muted">{exportGate.message}</p>}
        </div>
      </DrilldownModal>
    </div>
  );
}
